import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { GraduationCap, Paperclip, X } from 'lucide-react'

import { api } from '@/lib/api'
import type { ApiResponse } from '@/types/api'
import type { PublicRecommendation, RecommendationStatus } from '@/types/models'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

const PUBLIC_STATUS_OPTIONS: { value: RecommendationStatus; label: string; description: string }[] = [
  { value: 'IN_PROGRESS', label: 'In Progress', description: "I'm working on it" },
  { value: 'SUBMITTED', label: 'Submitted', description: 'I have submitted the letter' },
]

const STATUS_COLORS: Record<RecommendationStatus, string> = {
  NOT_REQUESTED: 'bg-muted text-muted-foreground',
  REQUESTED: 'bg-yellow-100 text-yellow-800',
  AGREED: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  SUBMITTED: 'bg-purple-100 text-purple-800',
  RECEIVED: 'bg-green-100 text-green-800',
}

const MAX_FILE_BYTES = 10 * 1024 * 1024

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiBase(): string {
  return (import.meta.env.VITE_API_URL as string | undefined) || '/api'
}

async function uploadLorDocument(token: string, file: File): Promise<void> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await fetch(`${getApiBase()}/v1/public/recommendation/${token}/upload`, {
    method: 'POST',
    body: formData,
  })
  if (!res.ok) {
    const json = await res.json().catch(() => ({}))
    throw new Error(json?.error?.message ?? 'File upload failed. Please try again.')
  }
}

// ---------------------------------------------------------------------------
// RecommenderPortalPage
// ---------------------------------------------------------------------------

/**
 * RecommenderPortalPage — public, no-auth page for recommenders.
 *
 * Accessed via /public/recommendation/:token
 * Shows student info, application details, and a status update form.
 * When "Submitted" is selected, an optional file upload appears so the
 * recommender can attach the letter for the student's reference.
 */
export default function RecommenderPortalPage() {
  const { token } = useParams<{ token: string }>()
  const [selectedStatus, setSelectedStatus] = useState<RecommendationStatus | ''>('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data, isLoading, isError, error } = useQuery<ApiResponse<PublicRecommendation>>({
    queryKey: ['public-recommendation', token],
    queryFn: () =>
      api.get<ApiResponse<PublicRecommendation>>(`/v1/public/recommendation/${token}`),
    enabled: !!token,
    retry: false,
  })

  const recommendation = data?.data

  const submitMutation = useMutation({
    mutationFn: async ({ status, file }: { status: RecommendationStatus; file: File | null }) => {
      if (file) {
        await uploadLorDocument(token!, file)
      }
      await api.post(`/v1/public/recommendation/${token}/status`, { status })
    },
    onSuccess: () => setSubmitted(true),
  })

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_FILE_BYTES) {
      setFileError('File must be smaller than 10 MB.')
      return
    }
    setFileError('')
    setSelectedFile(file)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedStatus) return
    submitMutation.mutate({ status: selectedStatus as RecommendationStatus, file: selectedFile })
  }

  const isExpiredLink =
    isError && (error as any)?.status === 410

  return (
    <div className="min-h-screen bg-background">
      {/* Logo bar */}
      <header className="flex items-center justify-center border-b py-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-blue">
            <GraduationCap className="h-5 w-5 text-white" />
          </div>
          <span className="text-lg font-bold tracking-tight text-foreground">GradNomad</span>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-lg px-4 py-10">
        {isLoading && (
          <div className="text-center py-12 text-sm text-muted-foreground">
            Loading recommendation details...
          </div>
        )}

        {isError && (
          <div className="rounded-xl border bg-card p-6 text-center">
            <p className="font-medium text-foreground">
              {isExpiredLink ? 'Link expired' : 'Link not found'}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {isExpiredLink
                ? 'This recommendation link has expired (links are valid for one year). Please contact the student for a new link.'
                : 'This recommendation link may be invalid. Please contact the student for a new link.'}
            </p>
          </div>
        )}

        {recommendation && (
          <div className="space-y-6">
            {/* Application details card */}
            <div className="rounded-xl border bg-card p-6 shadow-sm">
              <h1 className="text-lg font-semibold text-foreground">
                Letter of Recommendation Request
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                from {recommendation.studentFirstName}
              </p>

              <dl className="mt-5 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-sm text-muted-foreground">University</dt>
                  <dd className="text-sm font-medium text-foreground text-right">
                    {recommendation.universityName}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-sm text-muted-foreground">Program</dt>
                  <dd className="text-sm font-medium text-foreground text-right">
                    {recommendation.programName}
                  </dd>
                </div>
                {recommendation.deadline && (
                  <div className="flex items-start justify-between gap-4">
                    <dt className="text-sm text-muted-foreground">Application Deadline</dt>
                    <dd className="text-sm font-medium text-foreground text-right">
                      {new Date(recommendation.deadline).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </dd>
                  </div>
                )}
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-sm text-muted-foreground">Current Status</dt>
                  <dd>
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-xs font-medium',
                        STATUS_COLORS[recommendation.status] ?? 'bg-muted text-muted-foreground',
                      )}
                    >
                      {recommendation.status.replace(/_/g, ' ')}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Status update form */}
            <div className="rounded-xl border bg-card p-6 shadow-sm">
              {submitted ? (
                <div className="text-center py-4">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <svg
                      className="h-6 w-6 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <p className="font-semibold text-foreground">Status updated successfully</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Thank you for updating your status.{' '}
                    {recommendation.studentFirstName} has been notified.
                    {selectedFile && ' Your letter has been shared with them.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h2 className="font-semibold text-foreground">Update Your Status</h2>
                  <p className="text-sm text-muted-foreground">
                    Let {recommendation.studentFirstName} know where you are with the letter.
                  </p>

                  <div className="space-y-2">
                    {PUBLIC_STATUS_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-colors',
                          selectedStatus === option.value
                            ? 'border-brand-blue bg-brand-blue/5'
                            : 'hover:bg-muted/50',
                        )}
                      >
                        <input
                          type="radio"
                          name="status"
                          value={option.value}
                          checked={selectedStatus === option.value}
                          onChange={() => {
                            setSelectedStatus(option.value)
                            if (option.value !== 'SUBMITTED') {
                              setSelectedFile(null)
                              setFileError('')
                            }
                          }}
                          className="accent-brand-blue"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground">{option.label}</p>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Optional document upload — only when Submitted is selected */}
                  {selectedStatus === 'SUBMITTED' && (
                    <div className="rounded-lg border border-dashed p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-foreground">
                          Attach your letter{' '}
                          <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                        </p>
                        {selectedFile && (
                          <button
                            type="button"
                            onClick={() => { setSelectedFile(null); setFileError('') }}
                            className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                            aria-label="Remove file"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {selectedFile ? (
                        <div className="mt-2 flex items-center gap-2 text-sm text-foreground">
                          <Paperclip className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                          <span className="truncate">{selectedFile.name}</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="mt-2 text-sm text-brand-blue hover:underline focus:outline-none"
                        >
                          Choose file
                        </button>
                      )}

                      <p className="mt-1.5 text-xs text-muted-foreground">
                        PDF or DOCX — max 10 MB. The student will be able to download it.
                      </p>

                      {fileError && (
                        <p className="mt-1 text-xs text-destructive">{fileError}</p>
                      )}

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </div>
                  )}

                  {submitMutation.isError && (
                    <p className="text-sm text-destructive">
                      {(submitMutation.error as Error)?.message || 'Something went wrong. Please try again.'}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={!selectedStatus || submitMutation.isPending}
                    className="w-full rounded-md bg-brand-blue px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {submitMutation.isPending
                      ? (selectedFile ? 'Uploading & updating...' : 'Updating...')
                      : 'Update Status'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
